/**
 * Transformers for converting backend quiz data to frontend shape.
 */
import { QuizAIService } from '../../utils/quiz-ai-service';
const quizAIService = QuizAIService.getInstance();
const VALID_QUIZ_STATUSES = ['draft', 'published', 'archived'];
function toQuizStatus(s) {
    if (s && VALID_QUIZ_STATUSES.includes(s))
        return s;
    return 'draft';
}
/** Map backend difficulty strings to legacy format */
export const mapBackendDifficultyToLegacy = (difficulty) => {
    const map = {
        beginner: 'easy',
        intermediate: 'medium',
        advanced: 'hard',
        easy: 'easy',
        medium: 'medium',
        hard: 'hard',
    };
    if (!difficulty)
        return 'medium';
    return map[difficulty.toLowerCase()] || 'medium';
};
/**
 * Flatten a backend template to the frontend shape. Returns null for invalid items
 * so callers can filter instead of throwing (avoids Error Boundary on one bad template).
 */
export const flattenTemplate = (backendTemplate) => {
    try {
        if (!backendTemplate || typeof backendTemplate !== 'object') {
            return null;
        }
        if (!backendTemplate.id) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Template missing ID, skipping', backendTemplate);
            }
            return null;
        }
        const content = backendTemplate.content;
        const title = content?.title ?? 'Untitled Quiz';
        const description = content?.description ?? '';
        const questions = Array.isArray(content?.questions) ? content.questions : [];
        const flattened = {
            id: backendTemplate.id,
            type: backendTemplate.type,
            subject_id: backendTemplate.subject_id || undefined,
            classroom_id: backendTemplate.classroom_id,
            difficulty: backendTemplate.difficulty,
            created_by: backendTemplate.created_by,
            metadata: backendTemplate.metadata ?? {},
            created_at: backendTemplate.created_at,
            status: toQuizStatus(backendTemplate.status),
            title,
            description,
            questions: questions.map((q) => ({
                ...q,
                points: q?.points ?? 1,
            })),
            timer_enabled: backendTemplate.timer_enabled ?? false,
            timer_minutes: backendTemplate.timer_minutes,
            numberOfQuestions: questions.length,
            enableDiversity: false,
            updated_at: backendTemplate.updated_at ?? backendTemplate.created_at ?? new Date().toISOString(),
        };
        return flattened;
    }
    catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('Error flattening template, skipping:', error, backendTemplate);
        }
        return null;
    }
};
const toStringSafe = (val) => {
    return typeof val === 'string' ? val : (val?.text || val?.title || val?.statement || val?.description || JSON.stringify(val ?? ''));
};
/** Reference text for subjective types: worker/API may send answer, model_answer, or guidance only */
function subjectiveReferenceFromFormatted(formatted) {
    const f = formatted;
    const a = f?.answer != null ? String(f.answer).trim() : '';
    if (a)
        return a;
    const m = f?.model_answer != null ? String(f.model_answer).trim() : '';
    if (m)
        return m;
    const g = f?.guidance != null ? String(f.guidance).trim() : '';
    return g || '';
}
/**
 * Convert a backend question (from API response) to the frontend Question shape.
 */
export const buildQuestionFromBackend = (bq) => {
    const type = (bq?.question?.type || 'multiple-choice').toLowerCase().replace(/_/g, '-');
    const formatted = bq?.question?.formatted || {};
    const qText = toStringSafe(formatted.question);
    const difficulty = mapBackendDifficultyToLegacy(bq?.metadata?.difficulty);
    const baseMeta = {
        source: 'unified-ai',
        generatedAt: bq?.metadata?.generatedAt,
        subject: bq?.metadata?.subject,
        subcategory: bq?.metadata?.subcategory,
        region: bq?.metadata?.region,
        level: bq?.metadata?.level,
        qualityScore: bq?.metadata?.qualityScore,
        qualityPassed: bq?.metadata?.qualityPassed,
        validationLevel: bq?.metadata?.validationLevel,
        explanation: formatted?.explanation || formatted?.guidance,
    };
    const isValidQuestion = quizAIService.validateQuestionTypeCompliance({
        question: qText,
        correctAnswer: formatted?.answer,
        guidance: formatted?.guidance,
        model_answer: formatted?.model_answer,
        answerA: formatted.options?.[0],
        answerB: formatted.options?.[1],
        answerC: formatted.options?.[2],
        answerD: formatted.options?.[3],
        scenario: formatted?.scenario,
    }, type);
    if (!isValidQuestion) {
        if (process.env.NODE_ENV === 'development') {
            console.warn(`Question type compliance validation failed for type: ${type}`, {
                question: qText,
                type,
                formatted,
            });
        }
        baseMeta.validationFailed = true;
        baseMeta.validationError = `Question does not meet ${type} format requirements`;
    }
    if (type === 'multiple-choice') {
        const opts = formatted.options ?? [];
        const rawAns = formatted.answer;
        const resolvedAnswer = rawAns != null && String(rawAns).trim() !== ''
            ? String(rawAns).trim().toUpperCase()
            : 'A';
        const correctLetter = /^[ABCD]$/.test(resolvedAnswer) ? resolvedAnswer : 'A';
        return {
            id: bq.id,
            type: 'multiple_choice',
            difficulty,
            question: qText,
            answerA: opts[0] || '',
            answerB: opts[1] || '',
            answerC: opts[2] || '',
            answerD: opts[3] || '',
            correctAnswer: correctLetter,
            metadata: baseMeta,
        };
    }
    if (type === 'true-false') {
        const ansRaw = String(formatted?.answer ?? '').toLowerCase();
        const tfAns = ansRaw === 'true' || ansRaw === 't' ? 'true' : 'false';
        return {
            id: bq.id,
            type: 'true_false',
            difficulty,
            question: qText,
            correctAnswer: tfAns,
            metadata: baseMeta,
        };
    }
    if (type === 'short-answer') {
        return {
            id: bq.id,
            type: 'short_answer',
            difficulty,
            question: qText,
            correctAnswer: toStringSafe(formatted?.answer),
            metadata: baseMeta,
        };
    }
    if (type === 'open-ended') {
        const ref = subjectiveReferenceFromFormatted(formatted);
        return {
            id: bq.id,
            type: 'open_ended',
            difficulty,
            question: qText,
            correctAnswer: ref,
            metadata: baseMeta,
        };
    }
    if (type === 'scenario-based') {
        const ref = subjectiveReferenceFromFormatted(formatted);
        return {
            id: bq.id,
            type: 'scenario_based',
            difficulty,
            question: qText,
            scenario: toStringSafe(formatted?.scenario),
            correctAnswer: ref,
            guidance: toStringSafe(formatted?.guidance),
            metadata: baseMeta,
        };
    }
    return {
        id: bq.id,
        type: 'multiple_choice',
        difficulty,
        question: qText,
        answerA: '',
        answerB: '',
        answerC: '',
        answerD: '',
        correctAnswer: 'A',
        metadata: baseMeta,
    };
};
